import type { ScheduleTaskType } from '@risksmart-app/domain/src/types/consts';
import { ownerIds } from 'src/components/form';
import type { UserOrGroup } from 'src/schemas/global';

import { resolveScheduleWindows } from '@/utils/scheduler';

import type { ScheduleFormFieldData } from './scheduleFormSchema';

export interface CreateScheduleTargetInput {
  Type: ScheduleTaskType;
  TargetId: string;
  AssigneeUserIds: string[];
  AssigneeGroupIds: string[];
}

export interface CreateScheduleInput {
  Name: string;
  Description: null | string;
  OwnerUserIds: string[];
  OwnerGroupIds: string[];
  FirstStartDate: Date;
  FirstDueDate: Date;
  RRule?: string;
  Targets: CreateScheduleTargetInput[];
}

/** The same split `ownerIds` performs, under the target's field names. */
const assigneeIds = (
  assignees: UserOrGroup[]
): Pick<CreateScheduleTargetInput, 'AssigneeGroupIds' | 'AssigneeUserIds'> => {
  const { OwnerGroupIds, OwnerUserIds } = ownerIds({ Owners: assignees });

  return { AssigneeGroupIds: OwnerGroupIds, AssigneeUserIds: OwnerUserIds };
};

/**
 * Map validated form state onto the `createSchedule` mutation input. Throws
 * rather than emitting a partial payload — the schema rejects everything this
 * guards against, so a throw means the caller skipped validation.
 */
export const toCreateScheduleInput = (
  values: ScheduleFormFieldData
): CreateScheduleInput => {
  const schedule = resolveScheduleWindows(values);
  if (schedule === null) {
    throw new Error('Schedule dates and recurrence must be complete');
  }

  return {
    Name: values.Name,
    Description: values.Description || null,
    ...ownerIds(values),
    FirstStartDate: schedule.FirstStartDate,
    FirstDueDate: schedule.FirstDueDate,
    // A one-off omits the key entirely — neither null nor the one-off
    // sentinel; the server substitutes the sentinel itself.
    ...(schedule.rrule === null ? {} : { RRule: schedule.rrule }),
    Targets: values.Targets.map((target) => ({
      Type: target.Type,
      TargetId: target.value,
      ...assigneeIds(values.AssigneesByTarget[target.value] ?? []),
    })),
  };
};
